<script setup lang="ts">
import { useTemplateRef } from "vue";
import { z } from "zod";
import { UiFormBuilder, UiButton } from "@shopexperts/shared";
import type { BuilderRowConfig } from "@shopexperts/shared";
import type { Faq } from "./types/listingModels";

const props = defineProps<{ submitLabel?: string }>();

const emit = defineEmits<{
  (e: 'submit', value: Faq): void;
}>();

const schema = z.object({
  question: z.string().min(1, "Question is required"),
  answer: z.string().min(1, "Answer is required"),
});

const rows: BuilderRowConfig[] = [
  {
    columns: [
      { 
        name: 'question', 
        type: 'text', 
        label: 'Question', 
        hint: 'Enter the frequently asked question.', 
        props: { placeholder: 'e.g., What kind of results can you expect from working with Blue Horizons?' } 
      },
    ],
  },
  {
    columns: [
      { 
        name: 'answer', 
        type: 'textarea', 
        label: 'Answer', 
        hint: 'Provide a clear and helpful answer.', 
        props: { 
          placeholder: 'Write your answer here...', 
          rows: 4 
        } 
      },
    ],
  },
];

const model = defineModel<{ question: string; answer: string }>({ 
  default: { question: '', answer: '' } 
});

const formRef = useTemplateRef('formRef');
defineExpose({
  resetForm: () => formRef.value?.resetForm(),
});

function onSubmit(values: { question: string; answer: string }) {
  const payload: Faq = {
    id: null,
    question: values.question,
    answer: values.answer,
  };
  emit('submit', payload);
}
</script>

<template>
  <UiFormBuilder ref="formRef" :schema="schema" :rows="rows" scroll-to-first-error v-model="model" @submit="onSubmit">
    <template #actions>
      <UiButton type="submit" variant="primary" :label="props.submitLabel || 'Add FAQ'" />
    </template>
  </UiFormBuilder>
</template>
